import React, { useState, useEffect, useContext } from "react";
import { SEO, ISeoProps } from "../seo/seo";
import Header from "../header/header";
import Footer from "../footer/footer";
import SimpleFooter from "../footer/simple-footer";
import {
  Body,
  Content,
  ContentBackground,
  GlobalCss,
  HeaderBase,
} from "./layout.styled";
import { Global } from "@emotion/react";
import Helmet from "react-helmet";
import PrivacyBanner from "../privacy-banner/privacy-banner";
import { AnnouncementBanner } from "../announcement-banner/announcement-banner";
import { IHeaderProps } from "../header/header";
import { ILocalesProps, Locales } from "../locales/locales";
import ThemeContext from "../../utils/theme-context";
import { useThemeContext } from "../../utils/theme-context";
import { UserValidationContext } from "../../utils/user-validation-context";

interface LayoutProps {
  children: React.ReactNode;
  seo: ISeoProps;
  isGuttered: boolean;
  headerLinksUID?: string;
  headerCTAsUID?: string;
  simpleFooter?: boolean;
  showAnnouncementBanner?: boolean;
  disableSearch?: boolean;
  locales?: ILocalesProps;
  bgGradient?: boolean;
}

// Returns props for header. The header is rendered based on
// the selected content entry's uid for the header links and header ctas
// content types. These uid's are passed in from the template level. If
// uids are not passed in from the template, this function is hardcoded
//to return the default content entry uids.
const getHeaderProps = (props: LayoutProps): IHeaderProps => ({
  headerLinksUID: props.headerLinksUID || "default-links",
  headerCTAsUID: props.headerCTAsUID || "default-ctas",
  disableSearch: props.disableSearch || false,
  showAnnouncementBanner: props.showAnnouncementBanner || false,
});

const BodyElement = (props: LayoutProps) => {
  const { seo, locales, children, simpleFooter, bgGradient } = props;
  const {
    currentMode,
    setCurrentMode,
    currentTransitionPercent,
    setCurrentTransitionPercent,
  } = useThemeContext();
  const [scrollPosition, setScrollPosition] = useState(0);
  const user = useContext(UserValidationContext);

  const handleScroll = () => {
    const position = window.pageYOffset;
    setScrollPosition(position);
  };

  const handleTouch = () => {
    let position = window.pageYOffset;
    setScrollPosition(position);
    setTimeout(() => {
      position = window.pageYOffset;
      setScrollPosition(position);
    }, 2000);
  };

  useEffect(() => {
    window.addEventListener("scrollend", handleScroll, { passive: true });
    window.addEventListener("touchend", handleTouch, { passive: true });

    return () => {
      window.removeEventListener("scrollend", handleScroll);
      window.removeEventListener("touchend", handleTouch);
    };
  }, []);

  const scrollFinal = 200;
  let scrollPercent = 0;

  scrollPercent = scrollPosition / scrollFinal;

  let backgroundOpacity = scrollPercent > 1 ? 0 : 1 - scrollPercent;

  setCurrentTransitionPercent(backgroundOpacity);

  return (
    <Body
      isDarkMode={currentMode}
      bgOpacity={currentTransitionPercent}
      scrollPosition={scrollPosition}
      bgGradient={bgGradient}
    >
      <Helmet>
        <noscript>The Xometry app works best with JavaScript enabled!</noscript>
      </Helmet>
      <Global styles={GlobalCss} />
      <SEO {...seo} />
      {locales && <Locales {...locales} />}
      <HeaderBase
        isDarkMode={currentMode}
        bgOpacity={currentTransitionPercent}
        scrollPosition={scrollPosition}
      >
        {props.showAnnouncementBanner && <AnnouncementBanner />}
        <Header {...getHeaderProps(props)} />
      </HeaderBase>
      <ContentBackground>
        <Content {...props}>{children}</Content>
      </ContentBackground>
      {simpleFooter ? <SimpleFooter /> : <Footer />}
      <PrivacyBanner />
    </Body>
  );
};

const Layout = (props: LayoutProps) => {
  return (
    <ThemeContext>
      <BodyElement {...props} />
    </ThemeContext>
  );
};

export default Layout;
